import { supabase } from '@/lib/supabase';

const COLS = '*';

const toRef = (c) =>
  c ? { id: c.id, name: c.name, uf: c.uf, city: c.city, logoUrl: c.logo_url ?? null } : null;

// Busca as solicitações e, em seguida, os dados das empresas envolvidas
async function withCompanies(rows) {
  const ids = [...new Set((rows || []).flatMap((r) => [r.requester_company_id, r.target_company_id]))].filter(Boolean);
  let byId = {};
  if (ids.length) {
    const { data: comps, error } = await supabase
      .from('companies')
      .select('id, name, uf, city, logo_url')
      .in('id', ids);
    if (error) throw new Error(error.message);
    byId = Object.fromEntries((comps || []).map((c) => [c.id, c]));
  }
  return (rows || []).map((r) => ({
    id: r.id,
    status: r.status,
    message: r.message,
    createdAt: r.created_at,
    decidedAt: r.decided_at,
    closedAt: r.closed_at,
    requesterName: r.requester_name,
    requesterEmail: r.requester_email,
    requesterCompany: toRef(byId[r.requester_company_id]),
    targetCompany: toRef(byId[r.target_company_id]),
  }));
}

async function call(fn, params) {
  const { data, error } = await supabase.rpc(fn, params);
  if (error) throw new Error(error.message);
  return data;
}

export const contactService = {
  /** Pedido de contato feito pela sua empresa a outra. */
  async request(targetCompanyId, message) {
    await call('request_contact', { p_target: targetCompanyId, p_message: message?.trim() || null });
  },

  /** Pedidos recebidos pela sua empresa. */
  async received(companyId) {
    const { data, error } = await supabase
      .from('contact_requests')
      .select(COLS)
      .eq('target_company_id', companyId)
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return withCompanies(data);
  },

  /** Pedidos que a sua empresa enviou a outras. */
  async sent(companyId) {
    const { data, error } = await supabase
      .from('contact_requests')
      .select(COLS)
      .eq('requester_company_id', companyId)
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return withCompanies(data);
  },

  /** Aceitar ('ativa') ou recusar ('rejeitada'). Recusar também revoga um acesso já liberado. */
  async decide(requestId, decision) {
    await call('decide_contact_request', { p_id: requestId, p_decision: decision });
  },

  /** Encerrar um serviço já concluído (só depois do acesso liberado). */
  async close(requestId) {
    await call('close_contact_request', { p_id: requestId });
  },

  /** Remove o pedido da caixa (vale para quem pediu e para quem recebeu). */
  async remove(requestId) {
    await call('delete_contact_request', { p_id: requestId });
  },

  /** Contatos de uma empresa. Só retorna se você for o dono ou tiver acesso liberado. */
  async contacts(companyId) {
    const rows = await call('get_company_contacts', { p_company: companyId });
    return rows?.[0] || null;
  },
};